// import fetch from 'node-fetch'
import { host, rails, railsApi } from './endPoints';
// const urls = window.urls
// import { host, rails } from './endpoints'
import { reportError } from '../react/services/errorService';
import axios from 'axios';
import isSSR from 'venuescanner-js/services/isSSR';
if (isSSR()) global.fetch = Promise.resolve({});

const then = (returnData, fetchText) => async r => {
  if (isSSR()) return Promise.resolve({});
  if (r.status === 204) return r;
  if (r.status === 500) {
    reportError('api', new Error(`HTTP 500: ${r.url}`));
  }

  let body;
  if (fetchText) return r.text();
  try {
    body = await r.json();
  } catch (err) {
    return r.text();
  }
  if (!returnData) return body;

  if (body.errors) throw body.errors;
  if (body.err) throw body.err;
  if (body.data && body.data.errors) throw body.data.errors;
  if (body.status === false) throw new Error('Status was false');

  return body.data;
};
/** @module
 * @name api
 * @description REST module for simple API calls<br />Much has been replaced by the Controllers service, but there are still useful cases for this.
 * @example
 * import {postToApi} from 'venuescanner-js/services/api'
 * await postToApi('/endpoint',{
 *  name: 'myname'
 * })
 */

/** @function
 * @name post
 * @description
 * Make a HTTP POST request
 * @param {string} url The endpoint to call
 * @param {Object} [data={}] The data to place in the request body
 * @param {Object} [options={}] The options
 * @param {boolean} [options.returnData=true] If TRUE then response will return **data** from the response only
 * @param {boolean} [options.asAdmin=false] If TRUE then the request will be made using the Admin user, if available.
 * @returns {Promise<Object>}
 */
export const post = (
  url,
  data = {},
  { returnData = true, asAdmin = false, ...options } = {},
) => {
  if (isSSR()) return Promise.resolve({});

  // console.log({ data })
  const headers = {
    Accept: 'application/json',
  };
  if (window.data && window.data.viewerToken) {
    headers['X-Access-Token'] = window.data && window.data.viewerToken;
  }
  if (
    window.data &&
    window.data.viewerAdminToken &&
    window.data.viewerAdminToken !== window.data.viewerToken
  ) {
    if (asAdmin) {
      headers['X-Access-Token'] = window.data.viewerAdminToken;
    } else {
      headers['X-Admin-Access-Token'] = window.data.viewerAdminToken;
    }
  }
  if (data && !(data instanceof FormData))
    headers['Content-Type'] = 'application/json';
  return fetch(url, {
    method: 'POST',
    headers,
    // credentials: "same-origin",
    body: data instanceof FormData ? data : data && JSON.stringify(data),
    ...options,
  }).then(then(returnData));
};

/** @function
 * @name postToApi
 * @description
 * Make a HTTP POST request to the Backend worker
 * @param {string} path The endpoint to call, excluding the domain and any prefix slash
 * @param {Object} [data={}] The data to place in the request body
 * @param {Object} [options={}] The options
 * @param {boolean} [options.returnData=true] If TRUE then response will return **data** from the response only
 * @param {boolean} [options.asAdmin=false] If TRUE then the request will be made using the Admin user, if available.
 * @returns {Promise<Object>}
 */
export const postToApi = (path, ...args) => post(`${host}/${path}`, ...args);

/** @function
 * @name patch
 * @description
 * Make a HTTP PATCH request
 * @param {string} url The endpoint to call
 * @param {Object} [data={}] The data to place in the request body
 * @param {Object} [options={}] The options
 * @param {boolean} [options.returnData=true] If TRUE then response will return **data** from the response only
 * @param {boolean} [options.asAdmin=false] If TRUE then the request will be made using the Admin user, if available.
 * @returns {Promise<Object>}
 */
export const patch = (url, data, options = {}) =>
  post(url, data, {
    ...options,
    method: 'PATCH',
  });

/** @function
 * @name patchToApi
 * @description
 * Make a HTTP PATCH request to the Backend worker
 * @param {string} path The endpoint to call, excluding the domain and any prefix slash
 * @param {Object} [data={}] The data to place in the request body
 * @param {Object} [options={}] The options
 * @param {boolean} [options.returnData=true] If TRUE then response will return **data** from the response only
 * @param {boolean} [options.asAdmin=false] If TRUE then the request will be made using the Admin user, if available.
 * @returns {Promise<Object>}
 */
export const patchToApi = (path, ...args) => patch(`${host}/${path}`, ...args);

/** @function
 * @name put
 * @description
 * Make a HTTP PUT request
 * @param {string} url The endpoint to call, excluding the domain and any prefix slash
 * @param {Object} [data={}] The data to place in the request body
 * @param {Object} [options={}] The options
 * @param {boolean} [options.returnData=true] If TRUE then response will return **data** from the response only
 * @param {boolean} [options.asAdmin=false] If TRUE then the request will be made using the Admin user, if available.
 * @returns {Promise<Object>}
 */
export const put = (url, data, options = {}) =>
  post(url, data, {
    ...options,
    method: 'PUT',
  });

/** @function
 * @name putToApi
 * @description
 * Make a HTTP PUT request to the Backend worker
 * @param {string} path The endpoint to call, excluding the domain and any prefix slash
 * @param {Object} [data={}] The data to place in the request body
 * @param {Object} [options={}] The options
 * @param {boolean} [options.returnData=true] If TRUE then response will return **data** from the response only
 * @param {boolean} [options.asAdmin=false] If TRUE then the request will be made using the Admin user, if available.
 * @returns {Promise<Object>}
 */
export const putToApi = (path, ...args) => put(`${host}/${path}`, ...args);

/** @function
 * @name uploadToApi
 * @description
 * Make a HTTP POST request containing a file. An example implementation is used in [photoDrop.js]{@link https://gitlab.com/teamvenuescanner/core/-/blob/master/packages/js/vanilla/components/manage/components/photoDrop.js}
 * @param {string} path The endpoint to call, excluding the domain and any prefix slash
 * @param {file} file The file object as returned from a form input
 * @param {function} [progressCallback=] A function called on upload progress updates with the percentage of completion
 * @returns {Promise<Object>}
 */
export const uploadToApi = (
  path,
  file,
  progressCallback = () => {},
  ...args
) => {
  if (!file) throw new Error('File is missing in upload');

  const formData = new FormData();
  formData.append('file', file);
  const headers = {
    Accept: 'application/json',
  };
  if (window.data && window.data.viewerToken) {
    headers['X-Access-Token'] = window.data && window.data.viewerToken;
  }
  return axios.post(`${host}/${path}`, formData, {
    headers,
    onUploadProgress: function (progressEvent) {
      const percentCompleted = Math.round(
        (progressEvent.loaded * 100) / progressEvent.total,
      );
      progressCallback(percentCompleted);
    },
  });
};

/** @function
 * @name get
 * @description
 * Make a HTTP GET request
 * @param {string} url The endpoint to call
 * @param {Object} [options={}] The options
 * @param {boolean} [options.returnData=true] If TRUE then response will return **data** from the response only
 * @param {boolean} [options.asAdmin=false] If TRUE then the request will be made using the Admin user, if available.
 * @returns {Promise<Object>}
 */
export const get = (
  url,
  { returnData = true, asAdmin = false, ...options } = {},
) => {
  if (isSSR() || window.isTest) return Promise.resolve({});

  const headers = {
    Accept: 'application/json',
  };
  if (window.data && window.data.viewerToken) {
    headers['X-Access-Token'] = window.data && window.data.viewerToken;
  }
  if (
    window.data &&
    window.data.viewerAdminToken &&
    window.data.viewerAdminToken !== window.data.viewerToken
  ) {
    if (asAdmin) {
      headers['X-Access-Token'] = window.data.viewerAdminToken;
    } else {
      headers['X-Admin-Access-Token'] = window.data.viewerAdminToken;
    }
  }
  return fetch(url, {
    headers,
    credentials: 'same-origin',
    ...options,
  }).then(then(returnData));
};
/** @function
 * @name getToApi
 * @description
 * Make a HTTP GET request to the Backend worker
 * @param {string} path The endpoint to call, excluding the domain and any prefix slash
 * @param {Object} [options={}] The options
 * @param {boolean} [options.returnData=true] If TRUE then response will return **data** from the response only
 * @param {boolean} [options.asAdmin=false] If TRUE then the request will be made using the Admin user, if available.
 * @returns {Promise<Object>}
 */
export const getToApi = (path, ...args) => get(`${host}/${path}`, ...args);

/** @function
 * @name getHTML
 * @description
 * Make a HTTP GET request and return the HTML
 * @param {string} url The endpoint to call
 * @returns {Promise<Object>}
 */
export const getHTML = (url, { returnData = false, ...options } = {}) => {
  if (isSSR()) return Promise.resolve({});

  const fetchText = true;
  const headers = {};
  if (window.data && window.data.viewerToken) {
    headers['X-Access-Token'] = window.data && window.data.viewerToken;
  }
  return fetch(url, {
    headers,
    credentials: 'same-origin',
    ...options,
  }).then(then(returnData, fetchText));
};

/** @function
 * @name getToRails
 * @description
 * Make a HTTP GET request to the Ruby worker
 * @param {string} path The endpoint to call, excluding the domain and any prefix slash
 * @param {Object} [options={}] The options
 * @param {boolean} [options.returnData=true] If TRUE then response will return **data** from the response only
 * @param {boolean} [options.asAdmin=false] If TRUE then the request will be made using the Admin user, if available.
 * @returns {Promise<Object>}
 */
export const getToRails = (path, ...args) => get(`${rails}/${path}`, ...args);

/** @function
 * @name postToRails
 * @description
 * Make a HTTP POST request to the Backend worker
 * @param {string} path The endpoint to call, excluding the domain and any prefix slash
 * @param {Object} [data={}] The data to place in the request body
 * @param {Object} [options={}] The options
 * @param {boolean} [options.returnData=true] If TRUE then response will return **data** from the response only
 * @param {boolean} [options.asAdmin=false] If TRUE then the request will be made using the Admin user, if available.
 * @returns {Promise<Object>}
 */
export const postToRails = (path, ...args) => post(`${rails}/${path}`, ...args);
export const postToRailsApi = (path, ...args) =>
  post(`${railsApi}/${path}`, ...args);
